import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styles from './PageTable.module.css';
import dayjs from 'dayjs';
import { TableStateType } from '../../../utils/contextapi/TableProvider';
import { InnocodeButton, InnocodeChip, InnocodeDatatable, InnocodeInput, openInnocodeDatePickerRange } from '@intsoft/innocode-core';

export const ADMINROLE = {
  SYS_ADMIN: 'SYS_ADMIN',
  ADMIN_USER: 'ADMIN_USER'
}

const PageTable = ({ datas, pageNameMap, onAddUser, detailUser, context, onPrevPage, onNextPage, disablePrev, disableNext, pageNumber, totalCount, listSize, searchValue, setSearchValue, onSearch, isMobileWindow }) => {
  const [isInitialMount, setIsInitialMount] = useState(true);

  const dateView = (dateView) => { 
    const date = new Date(dateView);
    return dayjs(date).format('YYYY-MM-DD HH:mm:ss');
  };

  const parentPageView = (parentPageId) => { 
    return parentPageId ? (pageNameMap[parentPageId] || `ID: ${parentPageId}`) : '-';
  };

  // 날짜 범위 변경 시 데이터 재조회 (초기 렌더링 제외)
  useEffect(() => {
    if (isInitialMount) {
      setIsInitialMount(false);
      return;
    }

    if (context.state.dateRange[0] || context.state.dateRange[1]) {
      onSearch('pageName', searchValue);
    }
  }, [context.state.dateRange]);

  // 날짜 범위 선택 핸들러
  // const handleDateRangeClick = useCallback(async () => {
  //   const pickedDateRange = await openInnocodeDatePickerRange({
  //     initialDate: [context.state.dateRange[0], context.state.dateRange[1]],
  //     minDate: new Date(2020, 0, 1),
  //     maxDate: new Date(),
  //     mode: 'range',
  //   });

  //   console.log("pickedDateRange: ", pickedDateRange);
  //   const newStartDate = pickedDateRange != null && pickedDateRange.startDate ? new Date(pickedDateRange.startDate) : null;
  //   const newEndDate = pickedDateRange != null && pickedDateRange.endDate ? new Date(pickedDateRange.endDate) : null;

  //   if(newStartDate && newEndDate) {
  //     context.actions.contextDispatch({
  //       type: TableStateType.DATE_RANGE, 
  //       data: [newStartDate, newEndDate]
  //     });
  //   } 
  // }, [context.state.dateRange, context.actions]);

  // // 날짜 범위 포맷 함수
  // const formatDateRange = useCallback(() => {
  //   const start = context.state.dateRange[0];
  //   const end = context.state.dateRange[1];
    
  //   if (!start && !end) {
  //     return '날짜 범위 선택';
  //   }
    
  //   const startStr = start ? dayjs(start).format('YYYY-MM-DD') : '';
  //   const endStr = end ? dayjs(end).format('YYYY-MM-DD') : '';
    
  //   if (startStr && endStr) {
  //     return `${startStr} ~ ${endStr}`;
  //   } else if (startStr) {
  //     return startStr;
  //   } else if (endStr) {
  //     return endStr;
  //   }
    
  //   return '날짜 범위 선택';
  // }, [context.state.dateRange]);

  // 행 클릭 핸들러
  const handleRowClick = useCallback((row) => {
    detailUser(row.pageId);
  }, [detailUser]);

  // 페이지 변경 핸들러
  const handlePageChange = useCallback((newPage) => {
    if (newPage > pageNumber) {
      onNextPage();
    } else {
      onPrevPage();
    }
  }, [pageNumber, onNextPage, onPrevPage]);

  // 컬럼 정의
  const columns = useMemo(() => {
    const baseColumns = [
      {
        id: 'pageName',
        header: '페이지 명',
        accessorKey: 'pageName',
        enableSorting: true,
        cell: ({ row }) => <span>{row.pageName}</span>,
      },
    ];

    // 모바일이 아닐 때만 상위 페이지 컬럼 추가
    if (!isMobileWindow) {
      baseColumns.push({
        id: 'parentPageId',
        header: '상위 페이지',
        accessorKey: 'parentPageId',
        enableSorting: true,
        cell: ({ row }) => <span>{parentPageView(row.parentPageId)}</span>,
      });
    }

    baseColumns.push({
      id: 'pageUrl',
      header: 'Path',
      accessorKey: 'pageUrl',
      enableSorting: true,
      cell: ({ row }) => <span>{row.pageUrl}</span>,
    });

    // 모바일이 아닐 때만 등록일 컬럼 추가
    if (!isMobileWindow) {
      baseColumns.push({
        id: 'createdDate',
        header: '등록일',
        accessorKey: 'createdDate',
        enableSorting: true,
        cell: ({ row }) => <span>{dateView(row.createdDateView)}</span>,
      });
    }

    return baseColumns;
  }, [isMobileWindow, pageNameMap]);

  return (
    <div className={styles.tableSection}>
      <InnocodeDatatable
        className={styles.dataTable}
        data={datas}
        columns={columns}
        enableCustomHeader={true}
        customHeader={
          <div className={styles.customHeaderContainer}>
            <div className={styles.customHeaderSearchContainer}>
              <InnocodeInput
                className={styles.customHeaderSearchInput}
                placeholder="페이지명 검색"
                showTrailingContent={true}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    onSearch('pageName', searchValue);
                  }
                }}
                trailingContent={
                  <InnocodeButton
                    enablePadding={false}
                    size="xs"
                    theme="opacity"
                    designType="alternative"
                    iconOnly={true}
                    icon={
                      <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                        <path stroke="currentColor" strokeLinecap="round" strokeWidth="2" d="m21 21-3.5-3.5M17 10a7 7 0 1 1-14 0 7 7 0 0 1 14 0Z" />
                      </svg>
                    }
                    onClick={() => onSearch('pageName', searchValue)}
                  />
                }
              />
              {/* <div className={styles.datePickerWrapper}>
                <InnocodeChip
                  size="lg"
                  theme="line"
                  type="primary"
                  onClick={handleDateRangeClick}
                  icon={
                    <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
                      <path fillRule="evenodd" d="M5 5a1 1 0 0 0 1-1 1 1 0 1 1 2 0 1 1 0 0 0 1 1h1a1 1 0 0 0 1-1 1 1 0 1 1 2 0 1 1 0 0 0 1 1h1a1 1 0 0 0 1-1 1 1 0 1 1 2 0 1 1 0 0 0 1 1 2 2 0 0 1 2 2v1a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V7a2 2 0 0 1 2-2ZM3 19v-7a1 1 0 0 1 1-1h16a1 1 0 0 1 1 1v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Zm6.01-6a1 1 0 1 0-2 0 1 1 0 0 0 2 0Zm2 0a1 1 0 1 1 2 0 1 1 0 0 1-2 0Zm6 0a1 1 0 1 0-2 0 1 1 0 0 0 2 0Zm-10 4a1 1 0 1 1 2 0 1 1 0 0 1-2 0Zm6 0a1 1 0 1 0-2 0 1 1 0 0 0 2 0Zm2 0a1 1 0 1 1 2 0 1 1 0 0 1-2 0Z" clipRule="evenodd"/>
                    </svg>
                  }
                >
                  {formatDateRange()}
                </InnocodeChip>
              </div> */}
            </div>
            <div className={styles.customHeaderActionContainer}>
              <InnocodeButton
                designType="primary"
                theme="line"
                size="md"
                onClick={onAddUser}
              >
                페이지 등록
              </InnocodeButton>
            </div>
          </div>
        }
        enableTableHeader={true}
        enableSorting={true}
        enableCheckbox={false}
        enablePagination={true}
        manualPagination={true}
        pageSize={listSize}
        totalCount={totalCount}
        currentPage={pageNumber}
        onPageChange={handlePageChange}
        onRowClick={handleRowClick}
        emptyMessage="페이지 목록이 없습니다."
        rowClassName={styles.tableRow}
      />
    </div>
  );
};

export default PageTable;
